import { useMemo, useState } from "react";
import { Table, Tabs, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckCircleFilled,
  WarningFilled,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";

import type { Investigator } from "../types";
import LineageSection from "../components/LineageSection";
import type { InvestigatorLineage } from "../components/lineage.types";
import MasteryCommentDrawer from "@/views/CrossTrialSiteProfile/components/MasteryCommentDrawer";
import type { MasteryCommentContext } from "@/views/CrossTrialSiteProfile/components/MasteryCommentDrawer";
import MasteryItemModal from "@/components/DataMastery/MasteryItemModal";
import { useScopeParams } from "@/hooks/useScopeParams";
import { apiFetchJson } from "@/utils/apiFetch";

type MasteryRecord = {
  raw_person_id: string;
  nct_id: string;
  name_as_registered: string;
  affiliation_as_registered: string;
  role: string;
  specialty: string | null;
  confidence_pct: number | null;
  confidence_tier: string | null;
  key_signals: string | null;
  name_flag: string | null;
  affiliation_flag: string | null;
  source: "vivo_internal" | "ctgov";
  source_system: string;
  source_label: string;
};

type OpenIssue = {
  id: string;
  entity_key: string;
  issue_type: string;
  entity_display_name: string;
  status: string;
  priority: string;
  created_at: string | null;
};

type GoldenPerson = {
  person_id: string;
  canonical_name: string | null;
  institution: string | null;
  specialty: string | null;
  npi: string | null;
  merge_count: number | null;
  source_systems: string[];
  overall_confidence: number | null;
};

type InvestigatorMasteryEntry = {
  canonical_name: string | null;
  canonical_institution: string | null;
  canonical_specialty: string | null;
  golden: GoldenPerson | null;
  variants: MasteryRecord[];
  open_issues: OpenIssue[];
  lineage?: InvestigatorLineage;
};

async function fetchInvestigatorMastery(
  scope: string,
  id: string,
  invKey: string,
): Promise<InvestigatorMasteryEntry> {
  return apiFetchJson<InvestigatorMasteryEntry>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/investigator-mastery/${encodeURIComponent(invKey)}/`,
  );
}

type MasteryTabProps = {
  investigator: Investigator;
};

// Per-feed badge colours, mirroring the site profile's mastery table so the
// same feed reads the same on both surfaces.
const SOURCE_COLORS: Record<string, { bg: string; text: string }> = {
  AODM: { bg: "#E8F4FB", text: "#0C6E9B" },
  DQS: { bg: "#EEF4F8", text: "#2B5A6E" },
  Citeline: { bg: "#FFF7ED", text: "#9A3412" },
  alchemer: { bg: "#F0F4FF", text: "#3730A3" },
};

export default function InvestigatorMasteryTab({
  investigator,
}: MasteryTabProps) {
  const { scope, id } = useScopeParams();

  const {
    data: entry,
    isLoading,
    isError,
    refetch,
  } = useQuery<InvestigatorMasteryEntry>({
    queryKey: ["investigator-mastery", scope, id, investigator.inv_key],
    queryFn: () => fetchInvestigatorMastery(scope!, id!, investigator.inv_key),
    enabled: !!scope && !!id,
    staleTime: 5 * 60 * 1000,
  });

  const records = useMemo(() => entry?.variants ?? [], [entry]);
  const [drawerContext, setDrawerContext] =
    useState<MasteryCommentContext | null>(null);
  const [activeIssueId, setActiveIssueId] = useState<string | null>(null);

  const sortedRecords = useMemo(
    () =>
      [...records].sort((a, b) =>
        a.source === b.source ? 0 : a.source === "vivo_internal" ? -1 : 1,
      ),
    [records],
  );

  // Filter options built from the feeds actually present on this investigator,
  // so the list never offers a source with no rows behind it.
  const sourceFilters = useMemo(
    () =>
      [...new Set(records.map((r) => r.source_system).filter(Boolean))]
        .sort()
        .map((sys) => ({
          text:
            records.find((r) => r.source_system === sys)?.source_label ?? sys,
          value: sys,
        })),
    [records],
  );

  const avgConfidence = useMemo(() => {
    const valid = records.filter((r) => r.confidence_pct !== null);
    if (!valid.length) return null;
    return Math.round(
      valid.reduce((s, r) => s + r.confidence_pct!, 0) / valid.length,
    );
  }, [records]);

  // Prefer the pipeline's golden-person confidence (0–1); fall back to the
  // per-record average when unavailable.
  const overallConfidence =
    entry?.golden?.overall_confidence != null
      ? Math.round(entry.golden.overall_confidence * 100)
      : avgConfidence;

  const flaggedCount = records.filter(
    (r) => r.name_flag || r.affiliation_flag,
  ).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-[#8C8C8C] text-[13px]">
        Loading mastery records…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 h-48 text-[13px]">
        <span className="text-[#8C8C8C]">
          Couldn&apos;t load mastery records.
        </span>
        <button
          type="button"
          onClick={() => refetch()}
          className="text-[#0C98D0] hover:underline bg-transparent border-0 p-0 cursor-pointer"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!entry || records.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-[#8C8C8C] text-[13px]">
        No mastery records available for this investigator.
      </div>
    );
  }

  const colTitle = (label: string, tip: string) => (
    <Tooltip title={tip}>
      <span className="cursor-help border-b border-dashed border-[#9CA3AF] whitespace-nowrap">
        {label}
      </span>
    </Tooltip>
  );

  const columns: ColumnsType<MasteryRecord> = [
    {
      title: colTitle(
        "Source",
        "The feed this record was ingested from. Investigators are mastered across several feeds, so this is what tells two rows for the same person apart.",
      ),
      key: "source",
      width: 130,
      filters: sourceFilters,
      onFilter: (value, r) => r.source_system === value,
      render: (_, r) => {
        const colors = SOURCE_COLORS[r.source_system] ?? {
          bg: "#F3F4F6",
          text: "#374151",
        };
        return (
          <span
            className="inline-block rounded px-1.5 py-0.5 text-[11px] font-medium"
            style={{ backgroundColor: colors.bg, color: colors.text }}
          >
            {r.source_label || r.source_system || "—"}
          </span>
        );
      },
    },
    {
      title: colTitle(
        "Name as registered",
        "The investigator's name exactly as it appears in the source record. An amber warning icon indicates a discrepancy from the canonical name.",
      ),
      dataIndex: "name_as_registered",
      key: "name_as_registered",
      width: 200,
      render: (val: string, r) => (
        <div>
          <span className="text-[12px] text-[#1F2933]">{val}</span>
          {r.name_flag && (
            <Tooltip title={r.name_flag}>
              <span className="ml-1.5">
                <WarningFilled style={{ color: "#F59E0B", fontSize: 11 }} />
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: colTitle(
        "Confidence",
        "Entity match confidence score (≥85% green, ≥65% amber, <65% red). Hover a score to see the key signals that drove it.",
      ),
      key: "confidence",
      width: 110,
      align: "center" as const,
      render: (_, r) => {
        if (r.confidence_pct === null)
          return <span className="text-[11px] text-[#9CA3AF]">—</span>;
        const color =
          r.confidence_pct >= 85
            ? "#22C55E"
            : r.confidence_pct >= 65
              ? "#F59E0B"
              : "#EF4444";
        return (
          <Tooltip title={r.key_signals ?? undefined}>
            <span className="text-[12px] font-semibold" style={{ color }}>
              {r.confidence_pct}%
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: colTitle(
        "Affiliation as registered",
        "The institution or organization as it appears in the source record. An amber warning icon indicates a discrepancy from the canonical affiliation.",
      ),
      dataIndex: "affiliation_as_registered",
      key: "affiliation_as_registered",
      width: 240,
      render: (val: string, r) => (
        <div>
          <span className="text-[12px] text-[#4B5563]">{val}</span>
          {r.affiliation_flag && (
            <Tooltip title={r.affiliation_flag}>
              <span className="ml-1.5">
                <WarningFilled style={{ color: "#F59E0B", fontSize: 11 }} />
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: colTitle(
        "Role",
        "The investigator's role on this trial as listed in the source record.",
      ),
      dataIndex: "role",
      key: "role",
      width: 170,
      render: (val: string) => {
        const isPI = val === "Principal Investigator";
        return (
          <span
            className={`text-[11px] font-medium ${
              isPI ? "text-[#2B4752]" : "text-[#6B7280]"
            }`}
          >
            {val}
          </span>
        );
      },
    },
    {
      title: colTitle("NCT ID", "ClinicalTrials.gov trial identifier."),
      dataIndex: "nct_id",
      key: "nct_id",
      width: 120,
      render: (val: string) => (
        <span className="text-[12px] font-mono text-[#2B4752]">{val}</span>
      ),
    },
    {
      title: colTitle("TA", "Therapeutic area of the trial."),
      dataIndex: "therapeutic_area",
      key: "therapeutic_area",
      width: 120,
      render: (val: string | null) => (
        <span className="text-[12px] text-[#4B5563]">{val ?? "—"}</span>
      ),
    },
  ];

  return (
    <>
      <MasteryItemModal
        itemId={activeIssueId}
        onClose={() => setActiveIssueId(null)}
      />
      <div className="flex flex-col gap-4 px-4 pb-4">
        {/* Golden record panel */}
        <div className="rounded-[8px] border border-[#D1E8D5] bg-[#F6FBF7] p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <CheckCircleFilled style={{ color: "#4CAF50", fontSize: 16 }} />
            <span className="text-[13px] font-semibold text-[#1F2933]">
              Golden Record — {investigator.name}
            </span>
          </div>

          <div className="grid grid-cols-5 gap-4">
            <div>
              <div className="text-[10px] font-medium text-[#8C8C8C] uppercase tracking-wide mb-1">
                Canonical affiliation
              </div>
              <div className="text-[12px] text-[#1F2933]">
                {entry.canonical_institution ?? investigator.institution}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-medium text-[#8C8C8C] uppercase tracking-wide mb-1">
                Specialty
              </div>
              <div className="text-[12px] text-[#1F2933]">
                {entry.canonical_specialty ?? investigator.specialty}
              </div>
            </div>
            {entry.golden?.npi && (
              <div>
                <Tooltip title="National Provider Identifier as recorded on the golden person record. Shown only when an NPI is on file.">
                  <div className="text-[10px] font-medium text-[#8C8C8C] uppercase tracking-wide mb-1 cursor-help inline-block">
                    NPI
                  </div>
                </Tooltip>
                <div className="text-[12px] font-medium text-[#1F2933]">
                  {entry.golden.npi}
                </div>
              </div>
            )}
            <div>
              <Tooltip title="Mastering pipeline confidence that this golden person represents one real individual, across all merged source records.">
                <div className="text-[10px] font-medium text-[#8C8C8C] uppercase tracking-wide mb-1 cursor-help inline-block">
                  Overall confidence
                </div>
              </Tooltip>
              <div
                className="text-[12px] font-semibold"
                style={{
                  color:
                    overallConfidence == null
                      ? "#9CA3AF"
                      : overallConfidence >= 85
                        ? "#22C55E"
                        : overallConfidence >= 65
                          ? "#F59E0B"
                          : "#EF4444",
                }}
              >
                {overallConfidence !== null ? `${overallConfidence}%` : "—"}
              </div>
            </div>
            <div>
              <Tooltip title="Total number of source records resolved to this investigator's canonical identity.">
                <div className="text-[10px] font-medium text-[#8C8C8C] uppercase tracking-wide mb-1 cursor-help inline-block">
                  Records resolved
                </div>
              </Tooltip>
              <div className="text-[12px] font-semibold text-[#1F2933]">
                {entry.golden?.merge_count ?? records.length}
                {flaggedCount > 0 && (
                  <Tooltip
                    title={`${flaggedCount} record${flaggedCount > 1 ? "s" : ""} required manual resolution`}
                  >
                    <span className="ml-1.5 text-[#F59E0B] text-[11px] cursor-default">
                      ⚠ {flaggedCount} flagged
                    </span>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Open issues */}
        {entry.open_issues.length > 0 && (
          <div className="rounded-[8px] border border-[#FEE2E2] bg-[#FFF8F7] p-3 flex flex-col gap-2">
            <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[#C2410C]">
              <ExclamationCircleOutlined style={{ fontSize: 13 }} />
              {entry.open_issues.length} open data mastery issue
              {entry.open_issues.length !== 1 ? "s" : ""}
            </div>
            <div className="flex flex-col gap-1.5">
              {entry.open_issues.map((issue) => (
                <button
                  key={issue.id}
                  type="button"
                  onClick={() => setActiveIssueId(issue.id)}
                  className="flex items-center justify-between text-[12px] w-full text-left bg-transparent border-0 p-0 cursor-pointer hover:opacity-80"
                >
                  <span className="text-[#C2410C] hover:underline">
                    {issue.issue_type?.replace(/_/g, " ") ?? "—"} ·{" "}
                    {issue.entity_display_name}
                  </span>
                  <span
                    className={`text-[11px] font-medium ${issue.priority === "high" ? "text-[#C2410C]" : issue.priority === "medium" ? "text-[#B45309]" : "text-[#8C8C8C]"}`}
                  >
                    {issue.priority} priority · {issue.status}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sub-nav: one section at a time — the golden panel and open issues
            stay above, the detail reads sit behind these tabs. */}
        <Tabs
          defaultActiveKey="records"
          size="small"
          items={[
            {
              key: "records",
              label: `Trial records (${sortedRecords.length})`,
              children: (
                <div className="rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)] overflow-hidden">
                  <Table<MasteryRecord>
                    dataSource={sortedRecords}
                    columns={columns}
                    rowKey="raw_person_id"
                    size="small"
                    pagination={{
                      pageSize: 20,
                      showSizeChanger: false,
                      size: "small",
                    }}
                    rowClassName={(r) =>
                      r.source === "vivo_internal"
                        ? "bg-[#F6FBF7] cursor-pointer"
                        : "cursor-pointer"
                    }
                    onRow={(r) => ({
                      onClick: () =>
                        setDrawerContext({
                          recordKey: `inv:${r.nct_id}`,
                          nctId: r.nct_id,
                          nameAsRegistered: r.name_as_registered,
                          source: r.source,
                          contextLines: [
                            {
                              label: "Affiliation",
                              value: r.affiliation_as_registered,
                            },
                            ...(r.specialty
                              ? [{ label: "Specialty", value: r.specialty }]
                              : []),
                            { label: "NCT ID", value: r.nct_id },
                          ],
                        }),
                    })}
                    scroll={{ x: 1350, y: 480 }}
                  />
                </div>
              ),
            },
            ...(entry.lineage
              ? [
                  {
                    key: "agreement",
                    label: "Field agreement",
                    children: (
                      <LineageSection
                        lineage={entry.lineage}
                        section="agreement"
                      />
                    ),
                  },
                  {
                    key: "raw",
                    label: "Raw records by source",
                    children: (
                      <LineageSection
                        lineage={entry.lineage}
                        section="records"
                      />
                    ),
                  },
                  ...(entry.lineage.merge_evidence.length > 0
                    ? [
                        {
                          key: "evidence",
                          label: "Dedup evidence",
                          children: (
                            <LineageSection
                              lineage={entry.lineage}
                              section="evidence"
                            />
                          ),
                        },
                      ]
                    : []),
                ]
              : []),
          ]}
        />

        <MasteryCommentDrawer
          open={drawerContext !== null}
          onClose={() => setDrawerContext(null)}
          context={drawerContext}
        />
      </div>
    </>
  );
}
